/* eslint-disable no-console */
import { connect } from "react-redux";
import { useState, useEffect, useMemo, useCallback } from "react";

// @mui material components
import Card from "@mui/material/Card";
import Modal from "@mui/material/Modal";
import PropTypes from "prop-types";
import moment from "moment";

// Material Dashboard 2 PRO React components
import MDBox from "components/MDBox";
import CardTitle from "components/CardTitle";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import { useTranslation } from "react-i18next";

// Material Dashboard 2 PRO React examples
import DashboardLayout from "components/LayoutContainers/DashboardLayout";
import DashboardNavbar from "components/Navbars/DashboardNavbar";
import Footer from "components/Footer";
import DataTable from "components/DataTable";
import { useNavigate } from "react-router-dom";
import BookingSelector from "./BookingSelector";
import amenityUtils from "../project/components/amenitiesConfig/amenityUtils";

const {
  getStatusColor,
  formatAmenityEventsForTable,
  getActiveClientReservationIds,
  getClientReservationsById,
} = amenityUtils;

function CellDate({ value }) {
  const parsedValue = Number(value);
  return (
    <span>
      {Number.isFinite(parsedValue) ? moment(parsedValue).format("DD/MM/YYYY HH:mm") : "-"}
    </span>
  );
}

function CellStatus({ value }) {
  const { t } = useTranslation();

  const color = getStatusColor(value);

  return (
    <span
      style={{
        background: color,
        padding: "4px 10px",
        borderRadius: "12px",
        color: "white",
        fontSize: "12px",
        fontWeight: "bold",
        textTransform: "uppercase",
      }}
    >
      {t(value)}
    </span>
  );
}

function CellActive({ value }) {
  const { t } = useTranslation();
  const isActive = Boolean(value);

  return (
    <MDBox display="flex" alignItems="center" justifyContent="center">
      <MDBox
        component="span"
        title={isActive ? t("active") : t("inactive")}
        sx={{
          width: 12,
          height: 12,
          borderRadius: "50%",
          backgroundColor: isActive ? "#2ecc71" : "#7f8c8d",
        }}
      />
    </MDBox>
  );
}

function CellAssociatedServices({ value }) {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const visibleServices = (Array.isArray(value) ? value : []).filter(
    (item) => Number(item?.serviceUnits) > 0
  );

  if (!visibleServices.length) return <span>-</span>;

  return (
    <MDBox display="flex" gap={1} flexDirection="column" flexWrap="wrap">
      {visibleServices.map((item, index) => {
        const canNavigate = !!item?.projectId && !!item?.serviceId;

        return (
          <MDBox
            key={`${item?.serviceId || "service"}-${item?.eventId || index}`}
            component="button"
            type="button"
            onMouseDown={(event) => {
              event.stopPropagation();
            }}
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              if (!canNavigate) return;
              navigate(`/projects/${item.projectId}/service/${item.serviceId}`, {
                state: { initialDate: item?.start || Date.now() },
              });
            }}
            title={canNavigate ? t("goToServiceEvent") : t("serviceEventNotFound")}
            sx={{
              border: "none",
              borderRadius: "10px",
              px: 1.25,
              py: 0.75,
              color: "#fff",
              background: item?.statusColor || "#7f8c8d",
              cursor: canNavigate ? "pointer" : "not-allowed",
              opacity: canNavigate ? 1 : 0.7,
              textAlign: "left",
              fontSize: "0.75rem",
              fontWeight: 700,
              lineHeight: 1.35,
              width: "180px",
            }}
          >
            {`(${item?.serviceUnits ?? 0}) - ${item?.serviceName || ""}${
              Number(item?.serviceUnitCost) > 0 && item?.serviceUnitCostLabel
                ? ` - ${item?.serviceUnitCostLabel}`
                : ""
            }`}
          </MDBox>
        );
      })}
    </MDBox>
  );
}

const columns = ({ t }) => {
  const baseColumns = [
    { Header: t("active"), accessor: "active", Cell: CellActive, maxWidth: 20 },
    {
      Header: t("amenity"),
      accessor: "amenityName",
      maxWidth: 20,
    },
    {
      Header: t("apartment"),
      accessor: "apartmentName",
      maxWidth: 20,
    },
    {
      Header: t("client"),
      accessor: "clientName",
      maxWidth: 20,
    },
    {
      Header: t("project"),
      accessor: "projectName",
      maxWidth: 20,
    },
    {
      Header: t("state"),
      accessor: "status",
      Cell: CellStatus,
      maxWidth: 10,
    },
    {
      Header: t("unit"),
      accessor: "amenityRequestNumber",
      width: 40,
    },
    {
      Header: t("unitCost"),
      accessor: "amenityUnitCostLabel",
      maxWidth: 16,
    },
    {
      Header: t("associatedServices"),
      accessor: "associatedServicesCards",
      Cell: CellAssociatedServices,
      maxWidth: 40,
    },
    {
      Header: t("totalCost"),
      accessor: "totalCostLabel",
      maxWidth: 16,
    },
    {
      Header: t("startDate"),
      accessor: "start",
      Cell: CellDate,
      maxWidth: 10,
    },
    {
      Header: t("endDate"),
      accessor: "end",
      Cell: CellDate,
      maxWidth: 10,
    },
  ];

  return baseColumns;
};

function AmenityEvents({
  amenityEvents,
  amenities,
  services,
  serviceEventsByService,
  projects,
  apartments,
  clientReservations,
  isProjectManager,
  userProjects,
}) {
  const navigate = useNavigate();

  const { t } = useTranslation();

  const [amenityEventsRows, setAmenityEventsRows] = useState([]);
  const [openBookingSelector, setOpenBookingSelector] = useState(false);
  const [selectedStartDateTime, setSelectedStartDateTime] = useState(
    moment().format("YYYY-MM-DDTHH:mm")
  );
  const [selectedEndDateTime, setSelectedEndDateTime] = useState(
    moment().add(1, "week").format("YYYY-MM-DDTHH:mm")
  );

  const clientReservationsById = useMemo(
    () => getClientReservationsById(clientReservations),
    [clientReservations]
  );

  useEffect(() => {
    const formattedAmenityEventsRows = formatAmenityEventsForTable(
      amenityEvents,
      amenities,
      apartments,
      projects,
      clientReservationsById
    );

    setAmenityEventsRows(formattedAmenityEventsRows);
  }, [JSON.stringify(amenityEvents), projects, apartments, amenities, clientReservationsById]);

  const activeClientReservationIds = useMemo(
    () => getActiveClientReservationIds(clientReservations),
    [clientReservations]
  );

  const allEventsRows = useMemo(() => {
    const now = Date.now();

    return amenityEventsRows.map((amenityEventRow) => {
      const amenityEventEnd = Number(amenityEventRow?.end);

      return {
        ...amenityEventRow,
        active:
          activeClientReservationIds.has(amenityEventRow?.clientReservationId) &&
          Number.isFinite(amenityEventEnd) &&
          amenityEventEnd > now,
      };
    });
  }, [amenityEventsRows, activeClientReservationIds]);

  const selectedStartTimestamp = useMemo(
    () => (selectedStartDateTime ? moment(selectedStartDateTime).valueOf() : NaN),
    [selectedStartDateTime]
  );

  const selectedEndTimestamp = useMemo(
    () => (selectedEndDateTime ? moment(selectedEndDateTime).valueOf() : NaN),
    [selectedEndDateTime]
  );

  const filterEventsByDateRange = useCallback(
    (rows) =>
      (rows || []).filter((eventRow) => {
        const eventStart = Number(eventRow?.start);
        const eventEndCandidate = Number(eventRow?.end);
        const eventEnd = Number.isFinite(eventEndCandidate) ? eventEndCandidate : eventStart;

        if (!Number.isFinite(eventStart)) return false;

        if (Number.isFinite(selectedStartTimestamp) && eventEnd < selectedStartTimestamp) {
          return false;
        }

        if (Number.isFinite(selectedEndTimestamp) && eventStart > selectedEndTimestamp) {
          return false;
        }

        return true;
      }),
    [selectedEndTimestamp, selectedStartTimestamp]
  );

  const filteredAllEventsRows = useMemo(
    () => filterEventsByDateRange(allEventsRows),
    [allEventsRows, filterEventsByDateRange]
  );

  // const tableColumns = useMemo(
  //   () => columns({ t, showActiveColumn: tabValue === 1 }),
  //   [t, tabValue]
  // );
  const tableColumns = useMemo(() => columns({ t }), [t]);
  // const rowsToShow = tabValue === 0 ? filteredActiveEventsRows : filteredAllEventsRows;
  const rowsToShow = filteredAllEventsRows;
  // const titleToShow =
  //   tabValue === 0 ? t("amenityEventsWithActiveClientReservation") : t("amenityEvents");

  const titleToShow = t("amenityEvents");

  const add = () => {
    setOpenBookingSelector(true);
  };

  const handleBookingSelectorSubmit = ({ projectId, resourceId }) => {
    if (!projectId || !resourceId) return;

    setOpenBookingSelector(false);

    navigate(`/projects/${projectId}/amenity/${resourceId}`, {
      state: { initialDate: Date.now() },
    });
  };

  const resetDateRangeToThisWeek = () => {
    setSelectedStartDateTime(moment().format("YYYY-MM-DDTHH:mm"));
    setSelectedEndDateTime(moment().add(1, "week").format("YYYY-MM-DDTHH:mm"));
  };

  const sanitizeFilePart = (value = "") =>
    String(value)
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "")
      .slice(0, 80);

  const getAssociatedServiceEvent = (serviceId, clientReservationId, eventId) => {
    if (!serviceId || !eventId) return null;

    const eventsByService = serviceEventsByService?.[serviceId] || {};

    const directMatch = eventsByService?.[clientReservationId]?.[eventId];
    if (directMatch) return directMatch;

    const fallbackMatch = Object.values(eventsByService)
      .flatMap((eventsByReservation) => Object.values(eventsByReservation || {}))
      .find((serviceEvent) => serviceEvent?.id === eventId);

    return fallbackMatch || null;
  };

  const formatCurrencyValue = (value, currency) => {
    const parsedValue = Number(value);
    if (!Number.isFinite(parsedValue)) return "0";

    const formatted = Number.isInteger(parsedValue) ? `${parsedValue}` : parsedValue.toFixed(2);

    return currency ? `${formatted} ${currency}` : formatted;
  };

  const getEventCostInfo = (row = {}) => {
    const amenity = amenities?.[row?.amenityId] || {};
    const amenityUnitCost = Number(amenity?.cost) || 0;
    const amenityUnits = Number(row?.amenityRequestNumber) || 0;
    const amenityTotalCost = amenityUnitCost * amenityUnits;
    const amenityCurrency = amenity?.currency || "";

    const associatedServiceIds = row?.associatedServices || [];
    const associatedServiceEventIds = row?.associatedServiceEventIds || {};

    const associatedServicesCostRows = associatedServiceIds.map((serviceId) => {
      const service = services?.[serviceId] || {};
      const eventId = associatedServiceEventIds?.[serviceId];
      const serviceEvent = getAssociatedServiceEvent(serviceId, row?.clientReservationId, eventId);
      const reservation = clientReservationsById?.[row?.clientReservationId] || {};
      const projectId =
        serviceEvent?.projectId ||
        row?.projectId ||
        reservation?.projectId ||
        service?.projectId ||
        "";

      const serviceName = service?.name || serviceId || "";
      const serviceUnitCost = Number(service?.cost) || 0;
      const serviceUnits = Number(serviceEvent?.serviceRequestNumber) || 0;
      const serviceTotalCost = serviceUnitCost * serviceUnits;
      const serviceCurrency = service?.currency || amenityCurrency;
      const status = serviceEvent?.status || "pending";

      return {
        serviceName,
        serviceId,
        eventId,
        projectId,
        start: serviceEvent?.start,
        status,
        statusColor: getStatusColor(status),
        serviceUnits,
        serviceUnitCost,
        serviceTotalCost,
        serviceCurrency,
        serviceUnitCostLabel: formatCurrencyValue(serviceUnitCost, serviceCurrency),
      };
    });

    const associatedServicesSummary = associatedServicesCostRows
      .map(
        (serviceCostRow) =>
          `${serviceCostRow.serviceName} (${t("requestNumber")}: ${
            serviceCostRow.serviceUnits
          }, ${t("unitCost")}: ${serviceCostRow.serviceUnitCostLabel})`
      )
      .join(" | ");

    const associatedServicesTotal = associatedServicesCostRows.reduce(
      (accumulator, serviceCostRow) => accumulator + serviceCostRow.serviceTotalCost,
      0
    );

    const totalCost = amenityTotalCost + associatedServicesTotal;

    return {
      associatedServicesSummary: associatedServicesSummary || t("none"),
      associatedServicesCards: associatedServicesCostRows,
      amenityUnitCostLabel: formatCurrencyValue(amenityUnitCost, amenityCurrency),
      totalCostLabel: formatCurrencyValue(totalCost, amenityCurrency),
    };
  };

  const rowsForDisplay = useMemo(
    () =>
      rowsToShow.map((row) => ({
        ...row,
        clientName:
          clientReservationsById?.[row?.clientReservationId]?.displayName ||
          [
            clientReservationsById?.[row?.clientReservationId]?.firstName,
            clientReservationsById?.[row?.clientReservationId]?.lastName,
          ]
            .filter(Boolean)
            .join(" ") ||
          clientReservationsById?.[row?.clientReservationId]?.email ||
          "-",
        ...getEventCostInfo(row),
      })),
    [rowsToShow, amenities, services, serviceEventsByService, clientReservationsById]
  );

  const handleExportExcel = async () => {
    if (!rowsForDisplay?.length) return;

    const XLSX = await import("xlsx");
    const { saveAs } = await import("file-saver");

    const exportColumnDefs = [
      { label: t("amenity"), key: "amenityName" },
      { label: t("apartment"), key: "apartmentName" },
      { label: t("client"), key: "clientName" },
      { label: t("project"), key: "projectName" },
      { label: t("state"), key: "status" },
      { label: t("units"), key: "amenityRequestNumber" },
      { label: t("unitCost"), key: "amenityUnitCostLabel" },
      { label: t("associatedServices"), key: "associatedServicesSummary" },
      { label: t("totalCost"), key: "totalCostLabel" },
      { label: t("startDate"), key: "start" },
      { label: t("endDate"), key: "end" },
    ];

    const headers = exportColumnDefs.map((column) => column.label);

    const rowsToExcel = rowsForDisplay.map((row) => {
      const start = Number(row?.start);
      const end = Number(row?.end);

      return {
        amenityName: row?.amenityName || "",
        apartmentName: row?.apartmentName || "",
        clientName: row?.clientName || "-",
        projectName: row?.projectName || "",
        status: t(row?.status || ""),
        amenityRequestNumber: row?.amenityRequestNumber ?? "",
        associatedServicesSummary: row?.associatedServicesSummary || t("none"),
        amenityUnitCostLabel: row?.amenityUnitCostLabel || "0",
        totalCostLabel: row?.totalCostLabel || "0",
        start: Number.isFinite(start) ? moment(start).format("DD/MM/YYYY HH:mm") : "-",
        end: Number.isFinite(end) ? moment(end).format("DD/MM/YYYY HH:mm") : "-",
      };
    });

    const ws = XLSX.utils.json_to_sheet([], { header: headers });
    XLSX.utils.sheet_add_aoa(ws, [headers], { origin: "A1" });
    XLSX.utils.sheet_add_json(ws, rowsToExcel, { origin: "A2", skipHeader: true });

    ws["!cols"] = [
      { wch: 24 },
      { wch: 24 },
      { wch: 24 },
      { wch: 24 },
      { wch: 14 },
      { wch: 12 },
      { wch: 56 },
      { wch: 18 },
      { wch: 18 },
      { wch: 20 },
      { wch: 20 },
    ];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Data");

    const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });

    const blob = new Blob([excelBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    const uniqueProjectNames = [
      ...new Set(rowsForDisplay.map((row) => row?.projectName).filter(Boolean)),
    ];
    const projectNameForFile =
      uniqueProjectNames.length === 1
        ? uniqueProjectNames[0]
        : `${uniqueProjectNames.length || t("multiple")}-projects`;

    const startDateForFile = selectedStartDateTime
      ? moment(selectedStartDateTime).format("DD-MM-YYYY")
      : moment().format("DD-MM-YYYY");
    const endDateForFile = selectedEndDateTime
      ? moment(selectedEndDateTime).format("DD-MM-YYYY")
      : moment().format("DD-MM-YYYY");

    const fileName = `spaces-events-${sanitizeFilePart(
      projectNameForFile
    )}-${startDateForFile}-${endDateForFile}.xlsx`;

    saveAs(blob, fileName);
  };

  return (
    <DashboardLayout>
      <Modal
        open={openBookingSelector}
        onClose={() => setOpenBookingSelector(false)}
        style={{ overflow: "scroll" }}
      >
        <MDBox
          mt={3}
          sx={{
            width: { xs: "90vw", md: "38rem" },
            position: "absolute",
            left: "50%",
            transform: "translateX(-50%)",
          }}
        >
          <BookingSelector
            featureKey="amenities"
            resourceCollectionKey="amenities"
            resourceLabel="amenity"
            projects={projects}
            resources={amenities}
            isProjectManager={isProjectManager}
            userProjects={userProjects}
            onClose={() => setOpenBookingSelector(false)}
            onSubmit={handleBookingSelectorSubmit}
          />
        </MDBox>
      </Modal>

      <DashboardNavbar title={t("amenityEvents")} route="/amenities" />

      <MDBox pt={6} pb={3}>
        <Card>
          <CardTitle title={titleToShow} icon="group" />

          <MDBox px={3} pt={2} pb={1} display="flex" gap={2} flexWrap="wrap">
            <MDBox width="300px">
              <MDInput
                fullWidth
                type="datetime-local"
                label={t("startDate")}
                value={selectedStartDateTime}
                onChange={(event) => setSelectedStartDateTime(event.target.value)}
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </MDBox>

            <MDBox width="300px">
              <MDInput
                fullWidth
                type="datetime-local"
                label={t("endDate")}
                value={selectedEndDateTime}
                onChange={(event) => setSelectedEndDateTime(event.target.value)}
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </MDBox>

            <MDBox display="flex" alignItems="center">
              <MDButton
                variant="outlined"
                color="dark"
                size="small"
                onClick={resetDateRangeToThisWeek}
              >
                {t("thisWeek")}
              </MDButton>
            </MDBox>
          </MDBox>

          <DataTable
            table={{
              columns: tableColumns,
              rows: rowsForDisplay,
            }}
            canSearch
            canActuate
            canAdd
            canCustomActuate
            canCustomActuateWhenNoSelection
            customAction={handleExportExcel}
            customActionIcon="download"
            customActionDisabled={!rowsToShow?.length}
            add={add}
            action={(row) => {
              const { amenityId, start, clientReservationId } = row?.original || {};

              const reservation = clientReservationsById?.[clientReservationId] || {};
              const resolvedProjectId = reservation?.projectId || "";
              const resolvedApartmentId = reservation?.apartmentId || "";

              if (!resolvedProjectId || !amenityId || !resolvedApartmentId) return;

              navigate(
                `/projects/${resolvedProjectId}/amenity/${amenityId}/apartmentId/${resolvedApartmentId}`,
                {
                  state: { initialDate: start },
                }
              );
            }}
          />
        </Card>
      </MDBox>

      <Footer />
    </DashboardLayout>
  );
}

AmenityEvents.defaultProps = {
  amenityEvents: {},
  projects: {},
  apartments: {},
  amenities: {},
  services: {},
  serviceEventsByService: {},
  clientReservations: [],
  isProjectManager: false,
  userProjects: {},
};

AmenityEvents.propTypes = {
  amenityEvents: PropTypes.object,
  projects: PropTypes.object,
  apartments: PropTypes.object,
  amenities: PropTypes.object,
  services: PropTypes.object,
  serviceEventsByService: PropTypes.object,
  clientReservations: PropTypes.array,
  isProjectManager: PropTypes.bool,
  userProjects: PropTypes.object,
};

CellDate.defaultProps = {
  value: 0,
};

CellDate.propTypes = {
  value: PropTypes.any,
};

CellStatus.defaultProps = {
  value: "pending",
};
CellStatus.propTypes = {
  value: PropTypes.string,
};

CellActive.defaultProps = {
  value: false,
};
CellActive.propTypes = {
  value: PropTypes.any,
};

CellAssociatedServices.defaultProps = {
  value: [],
};
CellAssociatedServices.propTypes = {
  value: PropTypes.array,
};

const mapStateToProps = (state) => {
  const {
    mainUserReducer = {},
    clientReservationsReducer = {},
    apartmentsReducer = {},
  } = state || {};

  const isProjectManager = mainUserReducer?.isProjectManager || false;
  const userProjects = mainUserReducer?.projects || {};
  const clientReservationsObj = clientReservationsReducer?.clientReservations || {};

  let clientReservations = Object.values(clientReservationsObj).flatMap((item) =>
    item && typeof item === "object" ? Object.values(item) : []
  );

  if (isProjectManager) {
    clientReservations = clientReservations.filter(
      (reservation) => userProjects?.[reservation?.projectId]
    );
  }

  const amenityEventsReducer = state?.amenitiesReducer?.amenityEvents || {};
  const reservationsById = Object.values(clientReservationsObj).reduce(
    (accReservationsById, reservationsByClient) => ({
      ...accReservationsById,
      ...Object.values(reservationsByClient || {}).reduce(
        (accReservationItems, reservation) => ({
          ...accReservationItems,
          [reservation?.uid]: reservation,
        }),
        {}
      ),
    }),
    {}
  );

  const getAuthorizedProjectIdForEvent = (amenityEvent = {}, clientReservationId = "") => {
    const projectIdFromEvent = amenityEvent?.projectId;
    if (projectIdFromEvent) return projectIdFromEvent;

    const projectIdFromReservation = reservationsById?.[clientReservationId]?.projectId;
    if (projectIdFromReservation) return projectIdFromReservation;

    const apartmentId = amenityEvent?.apartmentId;
    const projectIdFromApartment = apartmentsReducer?.[apartmentId]?.projectId;
    if (projectIdFromApartment) return projectIdFromApartment;

    return "";
  };

  const isAuthorizedAmenityEvent = (amenityEvent = {}, clientReservationId = "") => {
    const projectId = getAuthorizedProjectIdForEvent(amenityEvent, clientReservationId);

    if (!projectId) {
      return false;
    }

    if (!userProjects?.[projectId]) {
      return false;
    }

    return true;
  };

  const filterEventsByReservation = (eventsById = {}, clientReservationId = "") =>
    Object.fromEntries(
      Object.entries(eventsById || {}).filter(([, amenityEvent]) =>
        isAuthorizedAmenityEvent(amenityEvent, clientReservationId)
      )
    );

  const filterAmenityEventsForProjectManager = (allAmenityEvents = {}) =>
    Object.fromEntries(
      Object.entries(allAmenityEvents || {})
        .map(([amenityId, eventsByClientReservation]) => {
          const filteredEventsByClientReservation = Object.fromEntries(
            Object.entries(eventsByClientReservation || {})
              .map(([clientReservationId, eventsById]) => [
                clientReservationId,
                filterEventsByReservation(eventsById, clientReservationId),
              ])
              .filter(([, filteredEventsById]) => Object.keys(filteredEventsById).length > 0)
          );

          return [amenityId, filteredEventsByClientReservation];
        })
        .filter(
          ([, filteredEventsByClientReservation]) =>
            Object.keys(filteredEventsByClientReservation).length > 0
        )
    );

  const amenityEvents = !isProjectManager
    ? amenityEventsReducer
    : filterAmenityEventsForProjectManager(amenityEventsReducer);

  return {
    amenityEvents,
    amenities: state?.amenitiesReducer?.amenities,
    services: state?.servicesReducer?.services,
    serviceEventsByService: state?.servicesReducer?.serviceEvents,
    projects: state?.projectsReducer,
    apartments: state?.apartmentsReducer,
    clientReservations,
    isProjectManager,
    userProjects,
  };
};

export default connect(mapStateToProps)(AmenityEvents);
